import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import { deleteTrainingDocService, getAllTrainingDocsService } from "../../../../Services/CRMAdminServices/CRMTrainingDocsService";
import { RootState } from '../../../../ReduxToolkit/Store';
import { useSelector } from 'react-redux';
const CRMTrainingDocsList = () => {
  const [docs, setDocs] = useState<any[]>([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const navigate = useSafeNavigate();
    const { RoleName, RoleID, nameidentifier: userID, FirstName } = useSelector((state: RootState) => state.adminAuth);

  const { data, isLoading, isError } = useGetRequestQuery<any>(
    ["getAllTrainingDocs"],
    getAllTrainingDocsService,
    { enabled: true }
  );

  useEffect(() => {
    if (data && !isError && !isLoading) {
      setDocs(data?.data || []);
    }
  }, [data, isError, isLoading]);

  const { mutate: deleteDoc, isPending: isDeleting } = useMutationRequestQuery(
    "deleteTrainingDoc",
    deleteTrainingDocService,
    {
      onSuccess: () => {
        toast.success("Training doc deleted successfully.");
        queryClient.invalidateQueries({ queryKey: ["getAllTrainingDocs"] });
        setDeleteModal(false);
      },
      onError: () => {
        toast.error("Failed to delete training doc.");
        setDeleteModal(false);
      },
    }
  );

  const confirmDelete = (id: number) => {
    setSelectedId(id);
    setDeleteModal(true);
  };

  const handleDeleteClick = () => {
    if (selectedId) {
      deleteDoc(selectedId);
    }
  };
const baseUrl = process.env.REACT_APP_API_BASE_URL;

const columns = [];

// Conditionally add the Action column
if (RoleID === '1') {
  columns.push({
    name: "Action",
    cell: (row: any) => (
      <ul className="action simple-list flex-row">
        <li className="edit">
          <Link to={`/EditTrainingDoc/${row.id}`}>
            <i className="icon-pencil-alt" />
          </Link>
        </li>
        <li className="delete">
          <a onClick={() => confirmDelete(row.id)} href="#">
            <i className="icon-trash" />
          </a>
        </li>
      </ul>
    ),
    width: "8rem",
  });
}

// Add other columns
columns.push(
  {
    name: "Document Name",
    selector: (row: any) => row.documentName,
    width: "20rem",
  },
  {
    name: "Document",
    cell: (row: any) => {
      const fileUrl = `${baseUrl}/docs/${row.documentFileName}`;
      const extension = row.documentFileName?.substring(row.documentFileName.lastIndexOf('.')) || "";
      const downloadName = `${row.documentName}${extension}`;

      return (
        <div className="d-flex flex-column align-items-start">
          <div className="d-flex gap-2 mt-1">
            <a
              href={fileUrl}
              download={downloadName}
              title={`Download ${row.documentName}`}
              style={{ fontSize: "18px", color: "#28a745" }}
            >
              <i className="icon-download" />
            </a>
          </div>
        </div>
      );
    },
    width: "25rem",
  }
);


  return (
    <div className="px-4 pt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 style={{color:'blue'}}>Training Documents</h3>
        {RoleID == '1' && (
        <Button color="primary" onClick={() => navigate("/AddTrainingDoc")}>
          Add Document
        </Button>
        )}
      </div>
      {isLoading ? (
        <Spinner color="primary" />
      ) : docs.length > 0 ? (
        <DataTable columns={columns} data={docs} responsive />
      ) : (
        <h6>No Training Docs available.</h6>
      )}

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)} centered>
        <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>Confirm Delete</ModalHeader>
        <ModalBody>Are you sure you want to delete this training doc?</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDeleteClick} disabled={isDeleting}>
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CRMTrainingDocsList;
